import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Card, CardContent, CardActions, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
  Chip, IconButton, Switch, FormControlLabel, Snackbar, Alert, CircularProgress,
  Grid, Divider, Tooltip
} from '@mui/material';
import {
  Add, Edit, Delete, PushPin, PushPinOutlined, Close, Campaign, Schedule,
  Visibility, VisibilityOff, ArrowBack
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const ALL_ROLES = ['ADMIN', 'DIRECTEUR', 'SCOLARITE', 'GESTIONNAIRE', 'ENSEIGNANT', 'ETUDIANT', 'CONSULTANT', 'CHEF_FILIERE', 'DIRECTION_FINANCIERE', 'DIRECTION_GENERALE', 'COORDINATEUR_PEDAGOGIQUE'];

const Annonces = () => {
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [form, setForm] = useState({
    title: '', body: '', targetRoles: ALL_ROLES, published: true, pinned: false, expiresAt: ''
  });
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchAnnonces = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/annonces`, { headers: authHeaders() });
      if (res.data.success) setAnnonces(res.data.data);
    } catch (err) {
      if (err.response?.status !== 404) console.error('Erreur annonces:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAnnonces(); }, [fetchAnnonces]);

  const resetForm = () => {
    setForm({ title: '', body: '', targetRoles: ALL_ROLES, published: true, pinned: false, expiresAt: '' });
    setEditing(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleOpenEdit = (a) => {
    setForm({
      title: a.title, body: a.body,
      targetRoles: a.targetRoles || ALL_ROLES,
      published: a.published, pinned: a.pinned,
      expiresAt: a.expiresAt ? a.expiresAt.slice(0, 16) : ''
    });
    setEditing(a._id);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      setSnackbar({ type: 'warning', text: 'Titre et contenu requis' }); return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.expiresAt) delete payload.expiresAt;
      if (editing) {
        await axios.put(`${API}/annonces/${editing}`, payload, { headers: authHeaders() });
        setSnackbar({ type: 'success', text: 'Annonce mise à jour' });
      } else {
        await axios.post(`${API}/annonces`, payload, { headers: authHeaders() });
        setSnackbar({ type: 'success', text: 'Annonce créée' });
      }
      setDialogOpen(false);
      resetForm();
      fetchAnnonces();
    } catch (err) {
      setSnackbar({ type: 'error', text: err.response?.data?.message || 'Erreur' });
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette annonce ?')) return;
    try {
      await axios.delete(`${API}/annonces/${id}`, { headers: authHeaders() });
      setSnackbar({ type: 'success', text: 'Annonce supprimée' });
      fetchAnnonces();
    } catch (err) {
      setSnackbar({ type: 'error', text: 'Erreur suppression' });
    }
  };

  const handleTogglePublish = async (a) => {
    try {
      await axios.put(`${API}/annonces/${a._id}`, { published: !a.published }, { headers: authHeaders() });
      fetchAnnonces();
    } catch (err) {
      setSnackbar({ type: 'error', text: 'Erreur' });
    }
  };

  const handleTogglePin = async (a) => {
    try {
      await axios.put(`${API}/annonces/${a._id}`, { pinned: !a.pinned }, { headers: authHeaders() });
      fetchAnnonces();
    } catch (err) {
      setSnackbar({ type: 'error', text: 'Erreur' });
    }
  };

  const isAdmin = user.role === 'ADMIN';

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>📢 Annonces</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Communications officielles de l'établissement
          </Typography>
        </Box>
        {isAdmin && (
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
            Nouvelle annonce
          </Button>
        )}
      </Box>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>
      ) : annonces.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, color: '#9ca3af' }}>
          <Campaign sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h6">Aucune annonce pour le moment</Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {annonces.map(a => (
            <Grid item xs={12} key={a._id}>
              <Card sx={{
                borderLeft: a.pinned ? '4px solid #8b5cf6' : a.published ? '4px solid #3b82f6' : '4px solid #d1d5db',
                opacity: a.published ? 1 : 0.6,
                bgcolor: a.pinned ? '#f5f3ff' : 'white'
              }}>
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {a.pinned && (
                        <Tooltip title="Épinglée">
                          <PushPin sx={{ fontSize: 20, color: '#8b5cf6' }} />
                        </Tooltip>
                      )}
                      {!a.published && (
                        <Chip icon={<VisibilityOff />} label="Brouillon" size="small" color="warning" />
                      )}
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>{a.title}</Typography>
                    </Box>
                    {isAdmin && (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title={a.pinned ? 'Détacher' : 'Épingler'}>
                          <IconButton size="small" onClick={() => handleTogglePin(a)}>
                            {a.pinned ? <PushPin fontSize="small" /> : <PushPinOutlined fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={a.published ? 'Dépublier' : 'Publier'}>
                          <IconButton size="small" onClick={() => handleTogglePublish(a)}>
                            {a.published ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modifier">
                          <IconButton size="small" onClick={() => handleOpenEdit(a)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton size="small" onClick={() => handleDelete(a._id)} color="error">
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ color: '#4b5563', whiteSpace: 'pre-wrap', mt: 1 }}>
                    {a.body}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                    <Chip icon={<Schedule />} label={format(new Date(a.createdAt), 'dd/MM/yyyy HH:mm', { locale: fr })} size="small" variant="outlined" />
                    <Chip label={`Par ${a.authorName || a.author?.prenom + ' ' + a.author?.nom || '?'}`} size="small" variant="outlined" />
                    {(a.targetRoles || []).map(r => (
                      <Chip key={r} label={r} size="small" sx={{ bgcolor: '#e0e7ff', color: '#4338ca', fontSize: '0.65rem' }} />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {editing ? 'Modifier l\'annonce' : 'Nouvelle annonce'}
            <IconButton onClick={() => setDialogOpen(false)}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Titre" fullWidth value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
            sx={{ mb: 2, mt: 1 }} required
          />
          <TextField
            label="Contenu" fullWidth multiline rows={6} value={form.body}
            onChange={e => setForm({ ...form, body: e.target.value })} required
          />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Cibles</InputLabel>
                <Select
                  multiple value={form.targetRoles} label="Cibles"
                  onChange={e => setForm({ ...form, targetRoles: e.target.value })}
                  renderValue={selected => selected.join(', ')}
                >
                  {ALL_ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Expire le" type="datetime-local" fullWidth
                value={form.expiresAt} onChange={e => setForm({ ...form, expiresAt: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
            <FormControlLabel
              control={<Switch checked={form.published} onChange={e => setForm({ ...form, published: e.target.checked })} />}
              label="Publiée"
            />
            <FormControlLabel
              control={<Switch checked={form.pinned} onChange={e => setForm({ ...form, pinned: e.target.checked })} />}
              label="Épinglée"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : (editing ? 'Mettre à jour' : 'Publier')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {snackbar ? <Alert severity={snackbar.type} onClose={() => setSnackbar(null)}>{snackbar.text}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default Annonces;


